import { useState } from 'react'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { useSubmissionAuditTrail } from '@/hooks/useAuditTrail'
import { AuditTrailTable } from '@/components/audit/AuditTrailTable'

/**
 * The audit trail for one submission — the whole of it, in one place.
 *
 * There were two panels here: this one, listing field edits from the 21 CFR
 * Part 11 form trail, and "Product transactions" below it, listing what those
 * edits produced. Reading one without the other left half the record
 * invisible, and reading both meant interleaving two tables by timestamp in
 * your head. The backend now serves them as one feed
 * (`GET /form-submissions/:id/audit`, see `common/audit-feed.ts`), so there is
 * one panel.
 *
 * Collapsed by default, and the count is on the header: it is a reference, not
 * something to scroll past on the way to completing a form, but an operator
 * should be able to tell there is something to open without opening it.
 *
 * There is no Refresh button. The query is fetched fresh every time the panel
 * is opened (`staleTime: 0` in the hook) and invalidated by every product
 * mutation, so the button only ever re-fetched what had just been fetched —
 * and a control that does nothing is worse than no control, because it implies
 * the panel goes stale without it.
 */
export function AuditTrailPanel({ submissionId }: { submissionId: string }) {
  const [open, setOpen] = useState(false)
  // `enabled` on the open state: the trail is two table reads and a user
  // lookup, and most people never open it.
  const trail = useSubmissionAuditTrail(submissionId, open)
  const rows = trail.data ?? []

  return (
    <div className='mt-6 rounded-lg border'>
      <button
        type='button'
        aria-expanded={open}
        className='flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/50'
        onClick={() => setOpen(o => !o)}
      >
        <span className='text-muted-foreground'>Audit Trail</span>
        {/* aria-hidden so the button's accessible name stays exactly "Audit
            Trail": the e2e smoke suite locates this panel by that name, and a
            count folded into it would change on every edit. Screen-reader
            users get the same thing they got before the count existed. */}
        <span aria-hidden className='flex items-center gap-2 text-xs text-muted-foreground'>
          {trail.isFetching && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
          {trail.isSuccess && `${rows.length} recorded`}
          {open ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
        </span>
      </button>

      {open && (
        <div className='border-t px-4 pb-4 pt-3'>
          {trail.isError && (
            <p className='mb-3 text-xs text-destructive'>
              {trail.error instanceof Error ? trail.error.message : 'Failed to load audit trail.'}
            </p>
          )}
          <AuditTrailTable
            rows={rows}
            isLoading={trail.isLoading}
            emptyMessage='No changes recorded yet.'
            // Its own layout key, so narrowing this panel does not narrow the
            // per-product dialog. Same columns as that dialog, all of them
            // shown — see AuditTrailTable.
            tableId='submission-audit-trail-all-columns-v2'
            maxHeightClass='max-h-[22rem]'
          />
        </div>
      )}
    </div>
  )
}
